import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Space, Typography,
  Tag, Popconfirm, Tooltip, message, Row, Col, Checkbox, Spin, Badge,
} from 'antd';
import {
  TeamOutlined, PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined,
  UnlockOutlined, CloseOutlined, SearchOutlined, LockOutlined,
  CheckCircleOutlined, InfoCircleOutlined, EyeOutlined,
} from '@ant-design/icons';
import { partnerAccountAPI, masterAPI, scheduleAPI } from '../services/api';
import { modalWidth, gray, radius } from '../theme/tokens';
import { EmptyState } from '../components/common';
import { SummerIcon, WinterIcon, SeasonBadge } from '../components/SeasonIcon';

interface PartnerAccount {
  id: number;
  username: string;
  full_name: string;
  company_id: number | null;
  company_name: string;
  company_code: string;
  created_at: string;
}

interface Company {
  id: number;
  name: string;
  code?: string;
}

interface GrantedSchedule {
  id: number;
  schedule_id: number;
  test_number: string;
  title: string;
  status: string;
  test_year: string;
  granted_at: string;
  note: string;
}

interface Schedule {
  id: number;
  test_number: string;
  title: string;
  status: string;
  test_year: string;
  company_name: string;
  summer_winter?: string;
}

const STATUS_COLOR: Record<string, string> = {
  '대기': '#94a3b8', '처리중': '#3b82f6', '진행중': '#10b981', '처리완료': '#6366f1',
  '조사완료': '#f59e0b', '성적서작성중': '#8b5cf6', '성적서완료': '#10b981',
  '발급완료': '#0ea5e9', '최종종료': '#64748b', '완료': '#059669', '중단': '#ef4444',
};

/* ── 계정 아바타 이니셜 ── */
const AccountAvatar: React.FC<{ name: string; size?: number }> = ({ name, size = 36 }) => {
  const initial = (name || '?')[0].toUpperCase();
  const colors = ['#6366f1', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b'];
  const bg = colors[initial.charCodeAt(0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.42, flexShrink: 0,
    }}>
      {initial}
    </div>
  );
};

const PartnerAccountPage: React.FC = () => {
  const [data, setData] = useState<PartnerAccount[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form] = Form.useForm();

  /* ── 조회 가능 목록 모달 state ── */
  const [visibleOpen, setVisibleOpen] = useState(false);
  const [visibleAccount, setVisibleAccount] = useState<PartnerAccount | null>(null);
  const [visibleList, setVisibleList] = useState<any[]>([]);
  const [visibleLoading, setVisibleLoading] = useState(false);
  const [visibleSearch, setVisibleSearch] = useState('');
  const [visibleTypeFilter, setVisibleTypeFilter] = useState<'전체' | '자동' | '수동'>('전체');

  /* ── 오픈 관리 모달 state ── */
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessAccount, setAccessAccount] = useState<PartnerAccount | null>(null);
  const [grantedList, setGrantedList] = useState<GrantedSchedule[]>([]);
  const [grantedLoading, setGrantedLoading] = useState(false);
  const [allSchedules, setAllSchedules] = useState<Schedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [rightSearch, setRightSearch] = useState('');
  const [rightPeriod, setRightPeriod] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [adding, setAdding] = useState(false);

  const PERIOD_PRESETS = [
    { key: '25h',   label: '25년 하', isDong: false, color: '#d97706', hoverBg: '#fef3c7', gradBg: 'linear-gradient(135deg,#fbbf24,#d97706)', years: ['25','2025'], sw: ['S','E','s','e','하'] },
    { key: '2526d', label: '25~26 동', isDong: true,  color: '#4f46e5', hoverBg: '#eef2ff', gradBg: 'linear-gradient(135deg,#818cf8,#4f46e5)', years: ['25','2025','26','2026'], sw: ['W','동','w'] },
    { key: '26h',   label: '26년 하', isDong: false, color: '#059669', hoverBg: '#ecfdf5', gradBg: 'linear-gradient(135deg,#34d399,#059669)', years: ['26','2026'], sw: ['S','E','s','e','하'] },
  ] as const;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, companiesRes] = await Promise.all([
        partnerAccountAPI.list(),
        masterAPI.companies.list(),
      ]);
      setData(accountsRes.data);
      setCompanies(companiesRes.data);
    } catch {
      message.error('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── 계정 CRUD ── */
  const handleCreate = () => { setEditId(null); form.resetFields(); setModalOpen(true); };
  const handleEdit = (r: PartnerAccount) => {
    setEditId(r.id);
    form.setFieldsValue({ username: r.username, full_name: r.full_name, company_id: r.company_id, password: '' });
    setModalOpen(true);
  };
  const handleDelete = async (id: number) => {
    try { await partnerAccountAPI.delete(id); message.success('삭제되었습니다.'); fetchData(); }
    catch (e: any) { message.error(e.response?.data?.detail || '삭제에 실패했습니다.'); }
  };
  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      if (editId) {
        const d: any = { full_name: v.full_name, company_id: v.company_id };
        if (v.password) d.password = v.password;
        await partnerAccountAPI.update(editId, d);
        message.success('수정되었습니다.');
      } else {
        await partnerAccountAPI.create(v);
        message.success('파트너 계정이 생성되었습니다.');
      }
      setModalOpen(false); fetchData();
    } catch (e: any) { if (e.response?.data?.detail) message.error(e.response.data.detail); }
  };

  /* ── 조회 가능 목록 모달 ── */
  const openVisibleModal = async (record: PartnerAccount) => {
    setVisibleAccount(record);
    setVisibleList([]);
    setVisibleSearch('');
    setVisibleTypeFilter('전체');
    setVisibleOpen(true);
    setVisibleLoading(true);
    try {
      const res = await partnerAccountAPI.visibleSchedules(record.id);
      setVisibleList(res.data);
    } catch {
      message.error('조회 가능 목록을 불러오는데 실패했습니다.');
    } finally {
      setVisibleLoading(false);
    }
  };

  /* ── 오픈 관리 모달 ── */
  const openAccessModal = async (record: PartnerAccount) => {
    setAccessAccount(record);
    setGrantedList([]);
    setSelectedIds([]);
    setRightSearch('');
    setRightPeriod(null);
    setAccessOpen(true);
    setGrantedLoading(true);
    try {
      const [grantedRes] = await Promise.all([
        partnerAccountAPI.scheduleAccess.list(record.id),
        allSchedules.length === 0 ? scheduleAPI.list().then(r => setAllSchedules(r.data)).catch(() => {}) : Promise.resolve(),
      ]);
      setGrantedList(grantedRes.data);
    } catch {
      message.error('데이터를 불러오지 못했습니다.');
    } finally {
      setGrantedLoading(false);
      if (allSchedules.length === 0) {
        setSchedulesLoading(true);
        scheduleAPI.list().then(r => setAllSchedules(r.data)).catch(() => {}).finally(() => setSchedulesLoading(false));
      }
    }
  };

  const handleAddAccess = async () => {
    if (!accessAccount || selectedIds.length === 0) return;
    setAdding(true);
    try {
      await partnerAccountAPI.scheduleAccess.add(accessAccount.id, selectedIds);
      const res = await partnerAccountAPI.scheduleAccess.list(accessAccount.id);
      setGrantedList(res.data);
      setSelectedIds([]);
      message.success(`${selectedIds.length}건 오픈 설정이 완료되었습니다.`);
    } catch (e: any) {
      message.error(e.response?.data?.detail || '오픈 설정에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAccess = async (scheduleId: number) => {
    if (!accessAccount) return;
    try {
      await partnerAccountAPI.scheduleAccess.remove(accessAccount.id, scheduleId);
      setGrantedList(prev => prev.filter(r => r.schedule_id !== scheduleId));
    } catch (e: any) {
      message.error(e.response?.data?.detail || '해제에 실패했습니다.');
    }
  };

  const grantedIds = useMemo(() => new Set(grantedList.map(r => r.schedule_id)), [grantedList]);

  const availableSchedules = useMemo(() => {
    const preset = rightPeriod ? PERIOD_PRESETS.find(p => p.key === rightPeriod) : null;
    return allSchedules.filter(s => {
      if (grantedIds.has(s.id)) return false;
      if (preset) {
        const yr = (s.test_year || '').trim();
        const sw = (s.summer_winter || '').trim();
        if (!(preset.years as readonly string[]).includes(yr)) return false;
        if (!(preset.sw as readonly string[]).includes(sw)) return false;
      }
      if (!rightSearch) return true;
      const kw = rightSearch.toLowerCase();
      return (
        s.test_number?.toLowerCase().includes(kw) ||
        s.title?.toLowerCase().includes(kw) ||
        s.company_name?.toLowerCase().includes(kw)
      );
    });
  }, [allSchedules, grantedIds, rightSearch, rightPeriod]);

  /* ── 테이블 컬럼 ── */
  const columns = [
    {
      title: '아이디',
      dataIndex: 'username',
      key: 'username',
      width: 140,       // 실데이터 최대 13자(taejunkorea11) 기준
      render: (v: string) => <Typography.Text strong style={{ color: '#4f46e5' }}>{v}</Typography.Text>,
    },
    {
      title: '담당자명',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 120,       // 한글 최대 9자 기준
    },
    {
      title: '의뢰사',
      dataIndex: 'company_name',
      key: 'company_name',
      minWidth: 140,    // 코드 Tag + 이름 가변, flex 확장
      render: (v: string, r: PartnerAccount) => v
        ? <Space size={4}><Tag color="blue" style={{ fontWeight: 600, marginRight: 0 }}>{r.company_code || v}</Tag>{r.company_code ? v : ''}</Space>
        : <Tag>미지정</Tag>,
    },
    {
      title: '조회 가능 목록',
      key: 'visible',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: PartnerAccount) => (
        <Button
          size="small"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => openVisibleModal(record)}
          style={{ color: '#0ea5e9', fontWeight: 500, padding: '0 4px' }}
        >
          목록 확인
        </Button>
      ),
    },
    {
      title: '오픈 관리',
      key: 'access',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: PartnerAccount) => (
        <Button
          size="small"
          type="dashed"
          icon={<UnlockOutlined />}
          onClick={() => openAccessModal(record)}
          style={{ color: '#7c3aed', borderColor: '#7c3aed', fontWeight: 500 }}
        >
          오픈 관리
        </Button>
      ),
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 155,       // 'YYYY. MM. DD. HH:MM' 24시간 기준
      render: (v: string) => v
        ? new Date(v).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
        : '-',
    },
    {
      title: '관리',
      key: 'actions',
      width: 70,        // 아이콘 버튼 2개
      align: 'center' as const,
      render: (_: any, record: PartnerAccount) => (
        <Space size={2}>
          <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} /></Tooltip>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)} okText="삭제" cancelText="취소">
            <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ── 우측 패널 테이블 ── */
  const rightColumns = [
    {
      title: '시험번호',
      dataIndex: 'test_number',
      key: 'test_number',
      width: 105,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#4f46e5', fontWeight: 600 }}>{v}</span>,
    },
    { title: '항목명', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 85,
      render: (v: string) => (
        <Tag style={{
          fontSize: 10, padding: '0 6px', lineHeight: '18px',
          background: (STATUS_COLOR[v] || '#94a3b8') + '20',
          color: STATUS_COLOR[v] || '#94a3b8',
          border: `1px solid ${(STATUS_COLOR[v] || '#94a3b8')}40`,
        }}>
          {v}
        </Tag>
      ),
    },
    { title: '연도', dataIndex: 'test_year', key: 'test_year', width: 50, render: (v: string) => <span style={{ fontSize: 11, color: gray[400] }}>{v}</span> },
  ];

  return (
    <Card
      title={
        <Space>
          <TeamOutlined style={{ color: '#4f46e5' }} />
          <span>파트너 계정 관리</span>
          <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>총 {data.length}건</Typography.Text>
        </Space>
      }
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>파트너 계정 추가</Button>}
    >
      {/* ── 조회 대상 안내 박스 ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20,
      }}>
        {/* 자동 조회 */}
        <div style={{
          background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '1px solid #bfdbfe',
          borderRadius: radius.lg, padding: '14px 18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: radius.md, background: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <CheckCircleOutlined style={{ color: '#fff', fontSize: 14 }} />
            </div>
            <Typography.Text strong style={{ fontSize: 13, color: '#1e40af' }}>자동 조회 (의뢰사 코드 매칭)</Typography.Text>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#1e3a8a', lineHeight: 2 }}>
            <li>파트너 계정에 설정된 <strong>의뢰사 코드</strong>와 일치하는 시험일정 자동 노출</li>
            <li>동일 코드의 여러 의뢰사(예: 지사·본사)도 통합 표시</li>
            <li>시험일정의 <strong>의뢰사</strong> 항목 기준으로 매칭</li>
          </ul>
        </div>
        {/* 수동 오픈 */}
        <div style={{
          background: 'linear-gradient(135deg,#faf5ff,#ede9fe)', border: '1px solid #ddd6fe',
          borderRadius: radius.lg, padding: '14px 18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: radius.md, background: '#7c3aed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <UnlockOutlined style={{ color: '#fff', fontSize: 14 }} />
            </div>
            <Typography.Text strong style={{ fontSize: 13, color: '#5b21b6' }}>수동 오픈 (오픈 관리)</Typography.Text>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#4c1d95', lineHeight: 2 }}>
            <li>협회 경유·기타 의뢰사 코드 불일치 시험은 자동 노출 불가</li>
            <li>각 계정의 <strong>오픈 관리</strong> 버튼으로 시험을 개별 지정</li>
            <li>지정된 시험은 자동 조회 목록과 합산하여 파트너에게 표시</li>
          </ul>
        </div>
      </div>

      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small"
        locale={{ emptyText: <EmptyState title="등록된 파트너 계정이 없습니다" size="sm" /> }}
        pagination={{ pageSize: 20, showTotal: (t) => `총 ${t}건` }} />

      {/* ── 계정 생성/수정 모달 ── */}
      <Modal
        title={<Space><KeyOutlined style={{ color: '#4f46e5' }} />{editId ? '파트너 계정 수정' : '파트너 계정 추가'}</Space>}
        open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)}
        okText={editId ? '수정' : '생성'} cancelText="취소" width={modalWidth.sm} maskClosable={false}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="username" label="아이디"
            rules={[{ required: true, message: '아이디를 입력하세요' }, { pattern: /^[a-zA-Z0-9_]+$/, message: '영문, 숫자, 밑줄만 사용 가능합니다' }]}>
            <Input placeholder="로그인 아이디" disabled={!!editId} />
          </Form.Item>
          <Form.Item name="password" label={editId ? '비밀번호 (변경 시에만 입력)' : '비밀번호'}
            rules={editId ? [] : [{ required: true, message: '비밀번호를 입력하세요' }]}>
            <Input.Password placeholder={editId ? '변경할 비밀번호 입력' : '비밀번호'} />
          </Form.Item>
          <Form.Item name="full_name" label="담당자명" rules={[{ required: true, message: '담당자명을 입력하세요' }]}>
            <Input placeholder="담당자 이름" />
          </Form.Item>
          <Form.Item name="company_id" label="의뢰사" rules={[{ required: true, message: '의뢰사를 선택하세요' }]}>
            <Select placeholder="의뢰사 선택" showSearch
              filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
              {companies.map(c => <Select.Option key={c.id} value={c.id}>{c.code ? `${c.code} - ${c.name}` : c.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 조회 가능 목록 모달 ── */}
      {(() => {
        const autoCount = visibleList.filter(s => s.access_type === '자동').length;
        const manualCount = visibleList.filter(s => s.access_type === '수동').length;
        const filtered = visibleList.filter(s => {
          if (visibleTypeFilter !== '전체' && s.access_type !== visibleTypeFilter) return false;
          if (!visibleSearch) return true;
          const kw = visibleSearch.toLowerCase();
          return s.test_number?.toLowerCase().includes(kw) || s.title?.toLowerCase().includes(kw) || s.company_name?.toLowerCase().includes(kw);
        });
        return (
          <Modal
            open={visibleOpen}
            onCancel={() => setVisibleOpen(false)}
            footer={<Button onClick={() => setVisibleOpen(false)}>닫기</Button>}
            width={modalWidth.lg}
            centered
            maskClosable={false}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: radius.md, background: 'linear-gradient(135deg,#0284c7,#0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EyeOutlined style={{ color: '#fff', fontSize: 15 }} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0c4a6e' }}>파트너 조회 가능 시험일정</div>
                  {visibleAccount && (
                    <div style={{ fontSize: 12, color: gray[500], fontWeight: 400, marginTop: 1 }}>
                      {visibleAccount.full_name} ({visibleAccount.username}) · {visibleAccount.company_name}
                    </div>
                  )}
                </div>
              </div>
            }
          >
            {/* 요약 배지 */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {[
                { label: '전체', count: visibleList.length, color: '#0ea5e9', bg: '#e0f2fe', border: '#bae6fd', key: '전체' as const },
                { label: '자동 매칭', count: autoCount,  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', key: '자동' as const },
                { label: '수동 오픈', count: manualCount, color: '#7c3aed', bg: '#faf5ff', border: '#ddd6fe', key: '수동' as const },
              ].map(b => (
                <button
                  key={b.key}
                  onClick={() => setVisibleTypeFilter(b.key)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${visibleTypeFilter === b.key ? b.color : b.border}`,
                    background: visibleTypeFilter === b.key ? b.color : b.bg,
                    transition: 'all 0.15s',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 700, color: visibleTypeFilter === b.key ? '#fff' : b.color }}>
                    {b.count}
                  </div>
                  <div style={{ fontSize: 11, color: visibleTypeFilter === b.key ? 'rgba(255,255,255,0.85)' : '#64748b', marginTop: 2 }}>
                    {b.label}
                  </div>
                </button>
              ))}
            </div>

            {/* 검색 */}
            <Input
              prefix={<SearchOutlined style={{ color: gray[400] }} />}
              placeholder="시험번호, 항목명, 의뢰사 검색..."
              value={visibleSearch}
              onChange={e => setVisibleSearch(e.target.value)}
              allowClear
              style={{ marginBottom: 10, borderRadius: 8 }}
            />

            {/* 목록 테이블 */}
            <Table
              dataSource={filtered}
              rowKey="id"
              size="small"
              loading={visibleLoading}
              scroll={{ y: 380 }}
              pagination={false}
              locale={{ emptyText: visibleLoading ? '불러오는 중...' : '조회 가능한 시험일정이 없습니다.' }}
              columns={[
                {
                  title: '구분',
                  dataIndex: 'access_type',
                  key: 'access_type',
                  width: 72,
                  align: 'center' as const,
                  render: (v: string) => (
                    <Tag style={{
                      fontSize: 10, padding: '0 6px', margin: 0, fontWeight: 600,
                      background: v === '자동' ? '#eff6ff' : '#faf5ff',
                      color: v === '자동' ? '#2563eb' : '#7c3aed',
                      border: `1px solid ${v === '자동' ? '#bfdbfe' : '#ddd6fe'}`,
                    }}>
                      {v === '자동' ? '자동' : '수동'}
                    </Tag>
                  ),
                },
                {
                  title: '시험번호',
                  dataIndex: 'test_number',
                  key: 'test_number',
                  width: 115,
                  render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: gray[700] }}>{v}</span>,
                },
                {
                  title: '항목명',
                  dataIndex: 'title',
                  key: 'title',
                  ellipsis: true,
                },
                {
                  title: '의뢰사',
                  dataIndex: 'company_name',
                  key: 'company_name',
                  width: 100,
                  ellipsis: true,
                  render: (v: string, r: any) => r.company_code
                    ? <><Tag color="blue" style={{ fontWeight: 600, marginRight: 2 }}>{r.company_code}</Tag><span style={{ fontSize: 11 }}>{v}</span></>
                    : <span style={{ fontSize: 11 }}>{v}</span>,
                },
                {
                  title: '상태',
                  dataIndex: 'status',
                  key: 'status',
                  width: 90,
                  render: (v: string) => (
                    <Tag style={{
                      fontSize: 10, padding: '0 6px', lineHeight: '18px', margin: 0,
                      background: (STATUS_COLOR[v] || '#94a3b8') + '20',
                      color: STATUS_COLOR[v] || '#94a3b8',
                      border: `1px solid ${(STATUS_COLOR[v] || '#94a3b8')}40`,
                    }}>
                      {v}
                    </Tag>
                  ),
                },
                {
                  title: '연도',
                  dataIndex: 'test_year',
                  key: 'test_year',
                  width: 48,
                  align: 'center' as const,
                  render: (v: string) => <span style={{ fontSize: 11, color: gray[400] }}>{v}</span>,
                },
                {
                  title: '작기',
                  dataIndex: 'summer_winter',
                  key: 'summer_winter',
                  width: 56,
                  align: 'center' as const,
                  render: (v: string) => <SeasonBadge value={v} size="sm" />,
                },
              ]}
            />
          </Modal>
        );
      })()}

      {/* ── 오픈 관리 모달 ── */}
      <Modal
        open={accessOpen}
        onCancel={() => setAccessOpen(false)}
        footer={null}
        width={modalWidth.xl}
        maskClosable={false}
        centered
        styles={{ body: { padding: 0 } }}
        title={null}
        closeIcon={null}
      >
        {/* ── 모달 헤더 ── */}
        <div style={{
          background: 'linear-gradient(135deg, #4c1d95, #7c3aed)',
          borderRadius: '8px 8px 0 0',
          padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: radius.lg,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <UnlockOutlined style={{ fontSize: 22, color: '#fff' }} />
            </div>
            <div>
              <div style={{ color: '#e9d5ff', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>
                파트너 시험일정 오픈 관리
              </div>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
                {accessAccount && (
                  <Space size={10}>
                    <AccountAvatar name={accessAccount.full_name} size={28} />
                    <span>{accessAccount.full_name}</span>
                    <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontWeight: 600, fontSize: 12 }}>
                      {accessAccount.company_code && `${accessAccount.company_code} · `}{accessAccount.company_name}
                    </Tag>
                  </Space>
                )}
              </div>
            </div>
          </div>
          <Button type="text" icon={<CloseOutlined style={{ color: '#e9d5ff', fontSize: 16 }} />}
            onClick={() => setAccessOpen(false)} style={{ padding: 8 }} />
        </div>

        {/* ── 안내 배너 ── */}
        <div style={{
          background: '#f5f3ff', borderBottom: '1px solid #ede9fe',
          padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <InfoCircleOutlined style={{ color: '#7c3aed', fontSize: 13 }} />
          <Typography.Text style={{ fontSize: 12, color: '#6d28d9' }}>
            <strong>의뢰사 코드</strong>로 자동 매칭된 시험은 이미 노출됩니다.
            협회경유·기타 수동 지정이 필요한 시험만 오른쪽 목록에서 선택하여 추가하세요.
          </Typography.Text>
        </div>

        {/* ── 본문: 좌/우 분할 ── */}
        <Row style={{ minHeight: 480 }}>
          {/* ── 좌측: 오픈된 시험 목록 ── */}
          <Col span={11} style={{ borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '14px 20px 10px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Space size={6}>
                <CheckCircleOutlined style={{ color: '#7c3aed' }} />
                <Typography.Text strong style={{ fontSize: 13, color: gray[800] }}>오픈된 시험</Typography.Text>
                <Badge count={grantedList.length} showZero
                  style={{ backgroundColor: grantedList.length > 0 ? '#7c3aed' : '#94a3b8', fontSize: 11 }} />
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>클릭하여 해제</Typography.Text>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {grantedLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Spin />
                </div>
              ) : grantedList.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ede9fe, #f5f3ff)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <LockOutlined style={{ fontSize: 26, color: '#a78bfa' }} />
                  </div>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>수동 오픈된 시험이 없습니다</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>오른쪽에서 시험을 선택하여 추가하세요</Typography.Text>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {grantedList.map(r => (
                    <div key={r.id} style={{
                      background: '#faf5ff', border: '1px solid #ede9fe', borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex', alignItems: 'center', gap: 10,
                      transition: 'box-shadow 0.15s',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{
                            fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
                            color: '#7c3aed', background: '#ede9fe', borderRadius: 4,
                            padding: '1px 6px',
                          }}>
                            {r.test_number}
                          </span>
                          <Tag style={{
                            fontSize: 10, padding: '0 5px', lineHeight: '16px', margin: 0,
                            background: (STATUS_COLOR[r.status] || '#94a3b8') + '20',
                            color: STATUS_COLOR[r.status] || '#94a3b8',
                            border: `1px solid ${(STATUS_COLOR[r.status] || '#94a3b8')}40`,
                          }}>
                            {r.status}
                          </Tag>
                        </div>
                        <div style={{ fontSize: 12, color: gray[700], fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.title}
                        </div>
                        <div style={{ fontSize: 10.5, color: gray[400], marginTop: 2 }}>
                          오픈일: {r.granted_at ? new Date(r.granted_at).toLocaleDateString('ko-KR') : '-'}
                        </div>
                      </div>
                      <Popconfirm
                        title="이 시험의 오픈을 해제하시겠습니까?"
                        onConfirm={() => handleRemoveAccess(r.schedule_id)}
                        okText="해제" cancelText="취소"
                        okButtonProps={{ danger: true }}
                      >
                        <Tooltip title="오픈 해제">
                          <Button type="text" size="small" danger icon={<CloseOutlined style={{ fontSize: 11 }} />}
                            style={{ flexShrink: 0, width: 28, height: 28, padding: 0 }} />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* ── 우측: 시험 선택 패널 ── */}
          <Col span={13} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Space size={6}>
                  <PlusOutlined style={{ color: '#0ea5e9' }} />
                  <Typography.Text strong style={{ fontSize: 13, color: gray[800] }}>시험 추가</Typography.Text>
                  {selectedIds.length > 0 && (
                    <Tag color="blue" style={{ fontWeight: 600 }}>{selectedIds.length}건 선택됨</Tag>
                  )}
                </Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  disabled={selectedIds.length === 0}
                  loading={adding}
                  onClick={handleAddAccess}
                  style={{
                    background: selectedIds.length > 0 ? 'linear-gradient(135deg,#818cf8,#4f46e5)' : undefined,
                    border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 12,
                    padding: '4px 14px',
                  }}
                >
                  오픈 추가 ({selectedIds.length})
                </Button>
              </div>
              <Input
                prefix={<SearchOutlined style={{ color: gray[400] }} />}
                placeholder="시험번호, 항목명, 의뢰사 검색..."
                value={rightSearch}
                onChange={e => setRightSearch(e.target.value)}
                allowClear
                size="small"
                style={{ borderRadius: 8, marginBottom: 8 }}
              />
              {/* 작기 필터 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10.5, fontWeight: 600, color: gray[400], letterSpacing: '0.06em', marginRight: 2 }}>작기</span>
                <button
                  onClick={() => setRightPeriod(null)}
                  style={{
                    padding: '3px 10px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: !rightPeriod ? 700 : 500,
                    border: `1px solid ${!rightPeriod ? '#64748b' : '#e2e8f0'}`,
                    background: !rightPeriod ? '#334155' : '#fff',
                    color: !rightPeriod ? '#fff' : '#94a3b8',
                    boxShadow: !rightPeriod ? '0 1px 4px rgba(51,65,85,0.2)' : 'none',
                    transition: 'all 0.12s',
                  }}
                >전체</button>
                {PERIOD_PRESETS.map(p => {
                  const isActive = rightPeriod === p.key;
                  const iconColor = isActive ? '#fff' : p.color;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setRightPeriod(isActive ? null : p.key)}
                      style={{
                        padding: '3px 11px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: isActive ? 700 : 500,
                        border: `1px solid ${isActive ? 'transparent' : p.color + '55'}`,
                        background: isActive ? p.gradBg : p.hoverBg,
                        color: isActive ? '#fff' : p.color,
                        boxShadow: isActive ? `0 2px 6px ${p.color}40` : 'none',
                        transition: 'all 0.12s',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      {p.isDong
                        ? <WinterIcon size={12} color={iconColor} />
                        : <SummerIcon size={12} color={iconColor} />}
                      {p.label}
                    </button>
                  );
                })}
                {rightPeriod && (
                  <span style={{ fontSize: 10.5, color: gray[500], background: gray[100], borderRadius: 8, padding: '2px 7px', marginLeft: 2 }}>
                    {availableSchedules.length}건
                  </span>
                )}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {schedulesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Spin tip="시험일정 불러오는 중..." />
                </div>
              ) : (
                <Table
                  dataSource={availableSchedules}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 360 }}
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedIds,
                    onChange: (keys) => setSelectedIds(keys as number[]),
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      setSelectedIds(prev =>
                        prev.includes(record.id)
                          ? prev.filter(x => x !== record.id)
                          : [...prev, record.id]
                      );
                    },
                    style: {
                      cursor: 'pointer',
                      background: selectedIds.includes(record.id) ? '#eef2ff' : undefined,
                    },
                  })}
                  columns={rightColumns}
                  locale={{ emptyText: rightSearch ? '검색 결과가 없습니다.' : '추가 가능한 시험이 없습니다.' }}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* ── 모달 푸터 ── */}
        <div style={{
          borderTop: '1px solid #f1f5f9', padding: '12px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fafbfc', borderRadius: '0 0 8px 8px',
        }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {grantedList.length > 0
              ? `총 ${grantedList.length}건의 시험이 수동으로 오픈되어 있습니다.`
              : '수동 오픈된 시험이 없습니다. 오른쪽에서 시험을 선택하여 추가하세요.'}
          </Typography.Text>
          <Button onClick={() => setAccessOpen(false)} style={{ borderRadius: 6 }}>닫기</Button>
        </div>
      </Modal>
    </Card>
  );
};

export default PartnerAccountPage;
